import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';

const API_BASE = API_BASE_URL;

// Per-candidate pool color overrides — same palette as the search pool page,
// so a color set here matches what shows there.
const PRESET_COLORS = [
  { key: 'red', hex: '#ef4444' },
  { key: 'orange', hex: '#f97316' },
  { key: 'yellow', hex: '#eab308' },
  { key: 'green', hex: '#22c55e' },
  { key: 'blue', hex: '#3b82f6' },
  { key: 'purple', hex: '#8b5cf6' },
  { key: 'pink', hex: '#ec4899' },
  { key: 'gray', hex: '#6b7280' },
];
const COLOR_MAP = Object.fromEntries(PRESET_COLORS.map(c => [c.key, c.hex]));

/**
 * PipelinesWidget
 * Shows all pipelines + search pools the candidate belongs to.
 * Works for both client and internal users.
 */
const PipelinesWidget = ({ profile, candidateId, onRefresh }) => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState([]);
  const [searchPools, setSearchPools] = useState([]);
  const [availablePipelines, setAvailablePipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);       // inline add panel open?
  const [selectedIds, setSelectedIds] = useState([]);    // multi-select of search ids
  const [addQuery, setAddQuery] = useState('');          // filter text
  const [adding, setAdding] = useState(false);
  // Pool status editing: the list of statuses + which row's menu is open.
  const [poolStatuses, setPoolStatuses] = useState([]);
  const [openStatusMenuId, setOpenStatusMenuId] = useState(null);
  const [openColorPicker, setOpenColorPicker] = useState(null);
  const [updatingPoolId, setUpdatingPoolId] = useState(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isClient = user.user_type === 'client';

  useEffect(() => {
    loadData();
  }, [candidateId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load pipeline cards
      const pipelinesUrl = isClient
        ? `/api/client/candidates/${candidateId}/pipelines`
        : `${API_BASE}/api/candidates/${candidateId}/pipelines`;
      const pRes = await fetch(pipelinesUrl, { headers });
      if (pRes.ok) {
        const data = await pRes.json();
        // Internal endpoint returns { pipelines: [...] }, client returns array directly
        setPipelines(data.pipelines || data || []);
      }

      // Load search pool memberships (internal only — clients don't see pools)
      if (!isClient) {
        try {
          const spRes = await fetch(`${API_BASE}/api/candidates/${candidateId}/search-pools`, { headers });
          if (spRes.ok) {
            const pools = await spRes.json();
            setSearchPools(pools);
            // Load the pool-status list once, using any pool's pipeline (the
            // status set is global, the pipelineId in the path is just routing).
            if (pools.length > 0 && poolStatuses.length === 0) {
              try {
                const stRes = await fetch(`${API_BASE}/api/pipelines/${pools[0].pipeline_id}/search-candidates/statuses`, { headers });
                if (stRes.ok) {
                  const cfg = await stRes.json();
                  setPoolStatuses(cfg.statuses || []);
                }
              } catch (e) { /* non-critical */ }
            }
          }
        } catch (e) { /* ignore if endpoint not available */ }
      }

      // Load available pipelines for "Add to" modal
      const allUrl = isClient ? '/api/client/pipelines' : `${API_BASE}/api/pipelines`;
      const allRes = await fetch(allUrl, { headers });
      if (allRes.ok) {
        const allPipelines = await allRes.json();
        const list = allPipelines.pipelines || allPipelines || [];
        // GET /api/pipelines already returns only active pipelines and does
        // NOT include a `status` field. Filtering on p.status === 'active'
        // here therefore removed everything, leaving the add list empty and
        // the button hidden. Keep any status filter tolerant: accept rows
        // with no status, and only drop ones explicitly marked inactive.
        setAvailablePipelines(list.filter(p => !p.status || p.status === 'active'));
      }
    } catch (err) {
      console.error('Error loading pipelines:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setIsAdding(true);
    setSelectedIds([]);
    setAddQuery('');
  };
  const cancelAdd = () => {
    setIsAdding(false);
    setSelectedIds([]);
    setAddQuery('');
  };

  const toggleSelected = (id) => {
    const key = String(id);
    setSelectedIds(prev =>
      prev.includes(key) ? prev.filter(x => x !== key) : [...prev, key]
    );
  };

  // Add the candidate to every selected search's POOL (longlist). The
  // endpoint takes one pipeline per call, so multi-select loops it. Each is
  // independent — a failure on one doesn't abandon the rest — and we report
  // the tally at the end rather than one alert per search.
  const handleAddToPools = async () => {
    if (!selectedIds.length) return;
    setAdding(true);
    const token = localStorage.getItem('authToken');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    let ok = 0;
    const failed = [];

    for (const id of selectedIds) {
      try {
        const url = isClient
          ? `/api/client/pipelines/${id}/candidates`
          : `${API_BASE}/api/pipelines/${id}/search-candidates`;
        const resp = await fetch(url, {
          method: 'POST',
          headers,
          body: JSON.stringify(isClient ? { candidate_id: candidateId } : { candidate_ids: [candidateId] }),
        });
        if (resp.ok) ok++;
        else {
          const d = await resp.json().catch(() => ({}));
          const name = availablePipelines.find(p => String(p.id) === String(id))?.name || `#${id}`;
          failed.push(`${name}: ${d.error || resp.status}`);
        }
      } catch (e) {
        const name = availablePipelines.find(p => String(p.id) === String(id))?.name || `#${id}`;
        failed.push(`${name}: ${e.message}`);
      }
    }

    setAdding(false);
    setIsAdding(false);
    setSelectedIds([]);
    setAddQuery('');
    loadData();
    if (onRefresh) onRefresh();

    if (failed.length) {
      alert(`Added to ${ok} search${ok === 1 ? '' : 'es'}.\nCouldn't add ${failed.length}:\n` + failed.join('\n'));
    }
  };

  const handlePromoteToPipeline = async (pipelineId) => {
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${API_BASE}/api/pipelines/${pipelineId}/search-candidates/to-pipeline`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ candidate_ids: [candidateId] })
      });
      if (resp.ok) {
        const result = await resp.json();
        if (result.promoted > 0) alert('Added to pipeline');
        else if (result.already_on_pipeline > 0) alert('Already on pipeline');
        loadData();
        if (onRefresh) onRefresh();
      }
    } catch (e) {
      console.error(e);
      alert('Failed to add to pipeline');
    }
  };

  // Update a candidate's status within a search pool. 'removed' is a valid
  // Per-candidate pool color override — mirrors the search pool page's
  // color dot. PUT /search-candidates/:candidateId/color.
  const changeColor = async (pipelineId, newColor) => {
    setOpenColorPicker(null);
    setSearchPools(prev => prev.map(sp => sp.pipeline_id === pipelineId ? { ...sp, color: newColor } : sp));
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${API_BASE}/api/pipelines/${pipelineId}/search-candidates/${candidateId}/color`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ color: newColor }),
      });
      if (!resp.ok) { await loadData(); return; }
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error(e);
      await loadData();
    }
  };

  // value the endpoint handles by deleting the pool membership, so Remove is
  // just another option in the same menu.
  const handlePoolStatusChange = async (pipelineId, newStatus) => {
    setUpdatingPoolId(pipelineId);
    setOpenStatusMenuId(null);
    // Optimistic update so the badge reflects the change immediately, before
    // the reload round-trips. If 'removed', drop the pool from the list.
    setSearchPools(prev => newStatus === 'removed'
      ? prev.filter(sp => sp.pipeline_id !== pipelineId)
      : prev.map(sp => sp.pipeline_id === pipelineId ? { ...sp, pool_status: newStatus } : sp)
    );
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${API_BASE}/api/pipelines/${pipelineId}/search-candidates/${candidateId}/status`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pool_status: newStatus }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        alert(err.error || 'Failed to update status');
        await loadData(); // revert optimistic change
        return;
      }
      await loadData();
      if (onRefresh) onRefresh();
    } catch (e) {
      console.error(e);
      alert('Failed to update status');
      await loadData(); // revert
    } finally {
      setUpdatingPoolId(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / 86400000);
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) { const w = Math.floor(diffDays / 7); return `${w} ${w === 1 ? 'week' : 'weeks'} ago`; }
    if (diffDays < 365) { const m = Math.floor(diffDays / 30); return `${m} ${m === 1 ? 'month' : 'months'} ago`; }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStageColor = (stageName) => {
    if (!stageName) return 'bg-gray-100 text-gray-700';
    const name = stageName.toLowerCase();
    if (name.includes('offer') || name.includes('hired') || name.includes('placed')) return 'bg-green-100 text-green-700';
    if (name.includes('final') || name.includes('onsite')) return 'bg-emerald-100 text-emerald-700';
    if (name.includes('reject') || name.includes('declined') || name.includes('withdraw')) return 'bg-red-100 text-red-700';
    if (name.includes('screen') || name.includes('phone') || name.includes('initial')) return 'bg-yellow-100 text-yellow-700';
    if (name.includes('interview')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const handlePipelineClick = (pipelineId) => {
    navigate(isClient ? `/client/pipeline/${pipelineId}` : `/search/${pipelineId}`);
  };

  // Pipelines the candidate is NOT already in (for add modal)
  const pipelineIds = new Set(pipelines.map(p => p.pipeline_id));
  const poolIds = new Set(searchPools.map(sp => sp.pipeline_id));
  const pipelinesNotIn = availablePipelines.filter(ap => !pipelineIds.has(ap.id) && !poolIds.has(ap.id));

  // Unified view: one entry per search (pipeline_id), merging the pool record
  // and the pipeline-card record so firm/search name is shown once and the
  // pool status + pipeline stage read as two facets of the same row. A search
  // can have a pool part, a pipeline part, or both.
  const unifiedSearches = (() => {
    const map = new Map();
    const ensure = (id) => {
      if (!map.has(id)) map.set(id, { pipeline_id: id, pool: null, pipe: null, firm_name: null, pipeline_name: null });
      return map.get(id);
    };
    // Pipeline cards first (they carry the richer stage/priority/timing data).
    pipelines.forEach(p => {
      const e = ensure(p.pipeline_id);
      e.pipe = p;
      e.firm_name = e.firm_name || p.firm_name;
      e.pipeline_name = e.pipeline_name || p.pipeline_name;
    });
    searchPools.forEach(sp => {
      const e = ensure(sp.pipeline_id);
      e.pool = sp;
      e.firm_name = e.firm_name || sp.firm_name;
      e.pipeline_name = e.pipeline_name || sp.pipeline_name;
    });
    // Sort: rows with a pipeline stage first, then pool-only, then by firm name.
    return Array.from(map.values()).sort((a, b) => {
      if (!!a.pipe !== !!b.pipe) return a.pipe ? -1 : 1;
      return (a.firm_name || a.pipeline_name || '').localeCompare(b.firm_name || b.pipeline_name || '');
    });
  })();

  // Relative "time in stage" from a timestamp → e.g. "4d in stage".
  const timeInStage = (ts) => {
    if (!ts) return null;
    const days = Math.floor((Date.now() - new Date(ts).getTime()) / 86400000);
    if (days < 1) return 'today';
    if (days === 1) return '1 day';
    if (days < 30) return `${days} days`;
    const months = Math.floor(days / 30);
    return months === 1 ? '1 month' : `${months} months`;
  };

  // Stage colors are stored as PALE background shades (e.g. #F3E5F5). Using the
  // pale color for text makes it unreadable, so derive a badge palette from it:
  // the pale color becomes a light background, and a darkened version of the
  // same hue becomes the text. Falls back gracefully if the value isn't a hex.
  const stageBadgeStyle = (hex) => {
    if (!hex || typeof hex !== 'string') return null;
    const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return null;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    // Perceived luminance (0–255). Pale colors are high; darken text a lot.
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    // Darken each channel toward 0 by a factor that scales with paleness, so a
    // very light fill still yields strong, readable text of the same hue.
    const factor = lum > 200 ? 0.42 : lum > 150 ? 0.5 : 0.62;
    const dark = (c) => Math.round(c * factor);
    const textColor = `rgb(${dark(r)}, ${dark(g)}, ${dark(b)})`;
    // Light background: the stored pale color itself, but if it's *too* saturated
    // to be pale, tint it toward white so text always reads.
    const bg = lum > 180 ? hex : `rgba(${r}, ${g}, ${b}, 0.18)`;
    return { backgroundColor: bg, color: textColor };
  };

  // Typeahead matches for the add modal — filter by search name or firm name.
  // Capped so a broad query doesn't render hundreds of rows at once.
  const addMatches = (() => {
    const q = addQuery.trim().toLowerCase();
    const base = q
      ? pipelinesNotIn.filter(p =>
          (p.name || '').toLowerCase().includes(q) ||
          (p.firm_name || '').toLowerCase().includes(q))
      : pipelinesNotIn;
    return base.slice(0, 50);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        <p className="text-sm">{error}</p>
        <button onClick={loadData} className="mt-2 text-xs text-emerald-700 hover:underline">Retry</button>
      </div>
    );
  }

  const totalCount = pipelines.length + searchPools.length;

  return (
    <div>
      {/* Add to search pool — recruiter action, so hidden for client users.
          Inline expand, matching the Positions widget: the dashed button
          swaps for a panel in place rather than opening a modal. */}
      {!isClient && !isAdding && pipelinesNotIn.length > 0 && (
        <button
          onClick={openAdd}
          className="w-full mb-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-green-400 hover:text-green-600 hover:bg-green-50 transition-colors flex items-center justify-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add to search
        </button>
      )}

      {/* Inline multi-select add panel */}
      {!isClient && isAdding && (
        <div className="mb-4 border border-green-200 rounded-lg p-3 bg-green-50">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-green-800">Add to search pools</h4>
            <button onClick={cancelAdd} className="text-gray-400 hover:text-gray-600" title="Cancel">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <p className="text-xs text-gray-500 mb-2">
            Adds this candidate to each selected search's longlist. Pick as many as you like.
          </p>

          {/* Filter box */}
          <input
            type="text"
            autoFocus
            value={addQuery}
            onChange={(e) => setAddQuery(e.target.value)}
            placeholder="Filter by search or firm…"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />

          {/* Scrollable multi-select list. Rows sit on white — the same
              surface as the filter box above — rather than a grey fill, so the
              panel reads as one continuous input area. Each row is two lines:
              firm/company on top (bold, 10px), search title beneath. */}
          <div className="mt-2 max-h-52 overflow-y-auto bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
            {addMatches.length === 0 ? (
              <p className="text-xs text-gray-400 px-3 py-4 text-center">
                {pipelinesNotIn.length === 0
                  ? 'This candidate is already in every active search.'
                  : 'No searches match that.'}
              </p>
            ) : (
              addMatches.map(p => {
                const checked = selectedIds.includes(String(p.id));
                return (
                  <label
                    key={p.id}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer bg-white border-l-2 transition-colors ${
                      checked ? 'border-green-500' : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleSelected(p.id)}
                      className="h-4 w-4 text-green-600 rounded border-gray-300 focus:ring-green-500 flex-shrink-0"
                    />
                    {/* Firm/company and search title share one line: firm bold
                        at 14px, title regular after it. The weight contrast
                        separates them; a "/" divider can slot in later if
                        needed. The whole line truncates with one ellipsis. */}
                    <span className="min-w-0 truncate" style={{ fontSize: '14px' }}>
                      {p.firm_name && <span className="font-bold text-gray-800">{p.firm_name}</span>}
                      {p.firm_name && <span className="text-gray-400"> </span>}
                      <span className="text-gray-500">{p.name}</span>
                    </span>
                  </label>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-2">
              <button onClick={handleAddToPools} disabled={!selectedIds.length || adding}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50">
                {adding ? 'Adding…' : `Add${selectedIds.length ? ` (${selectedIds.length})` : ''}`}
              </button>
              <button onClick={cancelAdd}
                className="px-3 py-1.5 border text-sm rounded hover:bg-gray-50">
                Cancel
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'None selected'}
            </span>
          </div>
        </div>
      )}

      {/* Tab bar removed — pipeline cards and pool cards render together. */}


      {totalCount === 0 ? (
        <div className="text-center py-6 text-gray-500">
          <svg className="w-10 h-10 mx-auto text-gray-300 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-sm">Not in any searches yet</p>
        </div>
      ) : (
        <>
          {/* Unified search rows: one card per search, merging the pool
              record and the pipeline-card record. Firm/search name shown once;
              pool status and pipeline stage read as facets of the same row. */}
          <div className="space-y-2">
          {unifiedSearches.map((row, index) => {
            const pipe = row.pipe;
            const pool = row.pool;
            const onPipeline = !!pipe || (pool && pool.on_pipeline);
            const curPool = pool ? poolStatuses.find(s => s.key === (pool.pool_status || 'research')) : null;
            const isMenuOpen = openStatusMenuId === row.pipeline_id;
            const addedByName = (pipe && pipe.added_by_name) || (pool && pool.added_by_name);
            const addedByType = pipe && pipe.added_by_type;
            const addedAt = (pipe && pipe.created_at) || (pool && pool.added_at);
            const inStage = pipe && timeInStage(pipe.entered_stage_at);
            return (
              <div
                key={row.pipeline_id || `s-${index}`}
                onClick={() => handlePipelineClick(row.pipeline_id)}
                className="border rounded-lg p-3 hover:shadow-md hover:border-emerald-300 transition-all cursor-pointer bg-white group"
              >
                {/* Header: firm / search name, and a right-side status badge */}
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm truncate group-hover:text-emerald-700 transition-colors min-w-0">
                        {row.firm_name && <span className="font-semibold text-gray-900">{row.firm_name}</span>}
                        {row.firm_name && <span className="text-gray-300"> / </span>}
                        <span className={row.firm_name ? 'text-gray-500' : 'font-semibold text-gray-900'}>{row.pipeline_name || 'Untitled Search'}</span>
                      </h4>
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-emerald-600 transition-colors flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Controls row: pool status + upgrade/priority + remove.
                    Internal users only; stops row-click navigation. */}
                {!isClient && (pool || pipe) && (
                  <div className="mt-2 flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                    {/* Color dot — per-candidate pool color, before the status.
                        Same palette as the search pool page. */}
                    {pool && (() => {
                      const isColorOpen = openColorPicker === row.pipeline_id;
                      const curColor = pool.color ? (COLOR_MAP[pool.color] || pool.color) : null;
                      return (
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setOpenColorPicker(isColorOpen ? null : row.pipeline_id); setOpenStatusMenuId(null); }}
                            className="w-5 h-5 rounded-full border-2 border-gray-200 flex items-center justify-center hover:border-gray-400 transition-colors flex-shrink-0"
                            title={pool.color ? `Color: ${pool.color}` : 'Set color'}
                          >
                            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: curColor || '#f3f4f6' }} />
                          </button>
                          {isColorOpen && (
                            <>
                              <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpenColorPicker(null); }} />
                              <div className="absolute z-20 left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 flex gap-1.5 flex-wrap w-24">
                                {PRESET_COLORS.map(c => (
                                  <button key={c.key} onClick={(e) => { e.stopPropagation(); changeColor(row.pipeline_id, c.key); }}
                                    className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${pool.color === c.key ? 'border-gray-800 ring-1 ring-gray-400' : 'border-transparent'}`}
                                    style={{ backgroundColor: c.hex }} title={c.key}
                                  />
                                ))}
                                <button onClick={(e) => { e.stopPropagation(); changeColor(row.pipeline_id, null); }}
                                  className="w-5 h-5 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center hover:border-gray-500"
                                  title="Clear color">
                                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })()}

                    {/* Pool status dropdown (with Remove) — only when pooled */}
                    {pool && (
                      <div className="relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); setOpenStatusMenuId(isMenuOpen ? null : row.pipeline_id); }}
                          disabled={updatingPoolId === row.pipeline_id}
                          className="text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap flex items-center gap-1 border disabled:opacity-50"
                          style={curPool ? { backgroundColor: `${curPool.color}18`, color: curPool.color, borderColor: `${curPool.color}40` } : {}}
                          title="Update pool status"
                        >
                          {updatingPoolId === row.pipeline_id ? 'Saving…' : (curPool?.label || 'Set status')}
                          <svg className={`w-3 h-3 flex-shrink-0 transition-transform ${isMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        {isMenuOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={(e) => { e.stopPropagation(); setOpenStatusMenuId(null); }} />
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[160px] max-h-64 overflow-y-auto">
                              {poolStatuses.map(st => (
                                <button key={st.key}
                                  onClick={(e) => { e.stopPropagation(); handlePoolStatusChange(row.pipeline_id, st.key); }}
                                  className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-gray-50 ${pool.pool_status === st.key ? 'bg-gray-50 font-medium' : ''}`}>
                                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: st.color }} />
                                  <span className="truncate">{st.label}</span>
                                </button>
                              ))}
                              <div className="my-1 border-t border-gray-100" />
                              <button
                                onClick={(e) => { e.stopPropagation(); if (window.confirm('Remove this candidate from the search pool?')) handlePoolStatusChange(row.pipeline_id, 'removed'); }}
                                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 text-red-600 hover:bg-red-50">
                                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                                Remove from pool
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Upgrade to pipeline — only if pooled but not yet on pipeline */}
                    {pool && !onPipeline && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handlePromoteToPipeline(row.pipeline_id); }}
                        className="text-xs px-2 py-1 rounded-full font-medium bg-emerald-100 text-emerald-700 hover:bg-emerald-200 whitespace-nowrap flex items-center gap-1"
                        title="Add to pipeline"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        To Pipeline
                      </button>
                    )}

                    {/* Single status badge: the client interview stage takes
                        over once the candidate reaches one (e.g. "In Review",
                        "Round 2"); before that, the pipeline stage shows
                        ("Format", "Sent"). Interview stage wins when present. */}
                    {pipe && (() => {
                      const onInterview = !!pipe.interview_stage_name;
                      const label = onInterview ? pipe.interview_stage_name : (pipe.stage_name || 'No Stage');
                      // Interview stages use a blue treatment; pipeline stages
                      // keep their configured stage color.
                      if (onInterview) {
                        return (
                          <span className="text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap bg-emerald-50 text-emerald-700 border border-emerald-100"
                            title="Client interview stage">
                            {label}
                          </span>
                        );
                      }
                      const badge = stageBadgeStyle(pipe.stage_color);
                      return (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${badge ? '' : getStageColor(pipe.stage_name)}`}
                          style={badge || undefined}
                          title="Pipeline stage"
                        >
                          {label}
                        </span>
                      );
                    })()}

                    {/* Priority pill — only when on the pipeline and non-default */}
                    {pipe && pipe.priority && pipe.priority !== 'medium' && (
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        pipe.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        pipe.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{pipe.priority.charAt(0).toUpperCase() + pipe.priority.slice(1)}</span>
                    )}
                  </div>
                )}

                {/* Metadata line: "Added by [name] [date]" + time-in-stage */}
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  {(addedByName || addedAt) && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      {addedByName ? (
                        <>
                          {addedByType === 'internal' ? 'Sent by ' : 'Added by '}
                          <span className="font-medium text-gray-700">{addedByName}</span>
                          {addedAt && <span className="text-gray-400">{' '}{formatDate(addedAt)}</span>}
                        </>
                      ) : (
                        <>Added {formatDate(addedAt)}</>
                      )}
                    </span>
                  )}
                  {inStage && (
                    <span className="flex items-center gap-1">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {inStage} in stage
                    </span>
                  )}
                </div>
              </div>
            );
          })}
          </div>

          <p className="text-xs text-gray-400 text-center pt-1">Click to view search details</p>
        </>
      )}

    </div>
  );
};

export default PipelinesWidget;
